// The Scripts IDE (spec/dashboard.md section 10.8; route `branches/:branch/extensions/:name?`): the tree by group, tabs for
// the open scripts, Monaco with the editor typings and the branch's model types, and the actions — Save (its own commit,
// never part of Save All; the host hot-reloads), Reset, Format, History, Run preSave on…, Delete, Create, Enable / Disable,
// Reload Extensions ("Run preSave on… → on the server" is a `documents:batch` dry run of the drafts). Reading is `content_read`; every write needs `content_write` on an unprotected branch.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ApiError, etagOfVersion } from '../../api/client';
import { ops } from '../../api/operations';
import { useAuth } from '../../auth/AuthContext';
import { useContent } from '../../content/ContentContext';
import { ReferencePicker } from '../../content/widgets/ReferenceField';
import { useExtensionHost, useExtensions } from '../../extensions/ExtensionsContext';
import { changedEntitiesOf, type HostState } from '../../extensions/host';
import { unnyIdOf } from '../../extensions/model';
import type { ChangedEntity } from '../../extensions/protocol';
import { modelTypings } from '../../extensions/typings';
import { useGame } from '../../shell/GameContext';
import { routes } from '../../shell/routes';
import { EmptyState } from '../../ui/Banner';
import { Button, IconButton } from '../../ui/Button';
import { useConfirm } from '../../ui/ConfirmDialog';
import { AlertCircle, AlertTriangle, Braces, FileCode2, History, Play, Plus, Puzzle, RefreshCw, RotateCcw, Save, Trash2, X } from '../../ui/icons';
import { useUrlParam } from '../../ui/Modal';
import { Menu } from '../../ui/Popover';
import { useToasts } from '../../ui/Toast';
import { queryCache, useQuery } from '../../workspace/cache';
import { useWorkspace, useWorkspaceVersion } from '../../workspace/react';
import { operationOf } from '../../workspace/save';
import { isTempId } from '../../workspace/Workspace';
import { CodeEditor, type CodeEditorHandle } from './CodeEditor';
import { CreateScriptDialog, type ExtensionDto } from './CreateScriptDialog';
import { HistoryDrawer } from './HistoryDrawer';
import { ideKey, isUnsaved, recallIde, rememberIde, settleIde } from './ideBuffers';
import { PreSaveReport, ServerPreSaveReport, verdictOf, type PreSaveRun, type ServerPreSaveRun } from './PreSaveReport';
import { ScriptsTree, type ExtensionSummary } from './ScriptsTree';
import './extensions.css';

interface Script {
  record: ExtensionDto | null;
  buffer: string;
  loading: boolean;
  error: string | null;
  saving: boolean;
}

export function hostStateText(state: HostState | undefined): string {
  if (!state || state.kind === 'idle') return 'not loaded';
  if (state.kind === 'loading') return 'loading…';
  if (state.kind === 'failed') return `failed: ${state.message}`;
  const n = state.extensions.length;
  return `${n} ${n === 1 ? 'extension' : 'extensions'} loaded${state.errors.length ? `, ${state.errors.length} failed` : ''}`;
}

const message = (e: unknown) => (e instanceof Error ? e.message : String(e));

export function ExtensionsPage() {
  const { branch = '' } = useParams();
  // a branch switch mounts the IDE of that branch (its own tabs and buffers, restored from ideBuffers.ts)
  return <ScriptsIde key={branch} branch={branch} />;
}

function ScriptsIde({ branch }: { branch: string }) {
  const { name: routeName } = useParams();
  const active = routeName ?? null;
  const navigate = useNavigate();
  const { client } = useAuth();
  const { org, game, can } = useGame();
  const workspace = useWorkspace();
  useWorkspaceVersion(workspace);
  const catalog = useContent();
  const contextHost = useExtensions();
  const { host: registeredHost } = useExtensionHost(workspace.branch); // re-renders on the host's state and log
  const host = contextHost ?? registeredHost;
  const toasts = useToasts();
  const confirm = useConfirm();
  const history = useUrlParam('history');
  const path = useMemo(() => ({ org, game: game.slug, branch }), [org, game.slug, branch]);

  const writable = can('content_write');
  const canWrite = writable && !workspace.isProtected;
  const missing = writable ? null : 'content_write';
  const protectedTitle = workspace.isProtected ? 'The branch is protected: changes go through a merge request.' : undefined;

  const listKey = branch ? `branch:${game.id}:${branch}:extensions` : null;
  const list = useQuery(listKey, () => client.data(ops.listExtensions, { path }));
  const items: ExtensionSummary[] = useMemo(() => list.data?.items ?? [], [list.data]);
  const refreshList = useCallback(() => {
    if (listKey) queryCache.invalidate(listKey);
  }, [listKey]);

  // the tabs and the unsaved buffers outlive the page: seeded from the IDE memory, written back on every change
  const memoryKey = ideKey(game.id, branch);
  const [open, setOpen] = useState<string[]>(() => recallIde(memoryKey)?.open ?? []);
  const [scripts, setScripts] = useState<Record<string, Script>>(() => {
    const kept = recallIde(memoryKey)?.scripts ?? {};
    return Object.fromEntries(Object.entries(kept).map(([n, s]) => [n, { record: s.record, buffer: s.buffer, loading: false, error: null, saving: false }]));
  });
  const scriptsRef = useRef(scripts);
  scriptsRef.current = scripts;
  useEffect(() => {
    rememberIde(memoryKey, open, scripts);
  }, [memoryKey, open, scripts]);
  const [conflict, setConflict] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [toggling, setToggling] = useState<Set<string>>(() => new Set());
  const [deleting, setDeleting] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [runAnchor, setRunAnchor] = useState<HTMLElement | null>(null);
  const [picking, setPicking] = useState(false);
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState<PreSaveRun | null>(null);
  const [serverReport, setServerReport] = useState<ServerPreSaveRun | null>(null);
  const editorRef = useRef<CodeEditorHandle>(null);

  const patch = useCallback((name: string, p: Partial<Script>) => {
    setScripts((s) => ({ ...s, [name]: { ...(s[name] ?? { record: null, buffer: '', loading: false, error: null, saving: false }), ...p } }));
  }, []);

  const fetchScript = useCallback(
    async (name: string) => {
      patch(name, { loading: true, error: null });
      try {
        const record = await client.data(ops.getExtension, { path: { ...path, name } });
        patch(name, { record, buffer: record.source ?? '', loading: false });
        return record;
      } catch (e) {
        patch(name, { loading: false, error: e instanceof ApiError && e.status === 404 ? `No extension named ${name} on this branch.` : message(e) });
        return null;
      }
    },
    [client, path, patch],
  );

  // the URL's script opens in a tab (and loads once)
  useEffect(() => {
    if (!active) return;
    setOpen((o) => (o.includes(active) ? o : [...o, active]));
    if (!scriptsRef.current[active]) void fetchScript(active);
  }, [active, fetchScript]);

  const at = useCallback((name: string | null) => routes.branch(org, game.slug, branch, name ? `extensions/${encodeURIComponent(name)}` : 'extensions'), [org, game.slug, branch]);
  const openScript = (name: string) => navigate(at(name));

  const reloadHost = useCallback(async () => {
    if (!host) return undefined;
    return host.load();
  }, [host]);

  const isDirty = isUnsaved;
  const dirty = useMemo(() => new Set(Object.entries(scripts).filter(([, s]) => isDirty(s)).map(([n]) => n)), [scripts]);

  // ---- tabs ----------------------------------------------------------------------------------------------------------

  const closeTab = async (name: string, force = false) => {
    if (!force && isDirty(scriptsRef.current[name])) {
      const ok = await confirm({ title: `Close ${name}?`, body: `${name} has unsaved changes; closing the tab discards them.`, confirmLabel: 'Discard and close', tone: 'danger' });
      if (!ok) return;
    }
    const index = open.indexOf(name);
    const next = open.filter((n) => n !== name);
    setOpen(next);
    setScripts((s) => {
      const copy = { ...s };
      delete copy[name];
      return copy;
    });
    if (conflict === name) setConflict(null);
    if (name === active) navigate(at(next[index] ?? next[index - 1] ?? null));
  };

  // ---- writes ----------------------------------------------------------------------------------------------------------

  const save = async (name: string, overwrite = false) => {
    const s = scriptsRef.current[name];
    if (!s?.record || !canWrite) return;
    let version: string | number = s.record.version;
    if (overwrite) {
      try {
        version = (await client.data(ops.getExtension, { path: { ...path, name } })).version;
      } catch (e) {
        toasts.error(message(e));
        return;
      }
    }
    const source = s.buffer;
    patch(name, { saving: true });
    try {
      const record = await client.data(ops.replaceExtensionSource, { path: { ...path, name }, body: { source, message: null }, ifMatch: etagOfVersion(version) });
      setScripts((all) => {
        const cur = all[name];
        if (!cur) return all;
        return { ...all, [name]: { ...cur, record: { ...record, source: record.source ?? source }, saving: false } };
      });
      settleIde(memoryKey, name, source);
      setConflict((c) => (c === name ? null : c));
      toasts.success(`Saved ${name} — extensions reloaded`);
      refreshList();
      void reloadHost();
    } catch (e) {
      patch(name, { saving: false });
      if (e instanceof ApiError && e.isVersionConflict) setConflict(name);
      else toasts.error(message(e), { requestId: e instanceof ApiError ? e.requestId : undefined });
    }
  };

  const reloadFromServer = async (name: string) => {
    setConflict(null);
    await fetchScript(name);
  };

  const reset = (name: string) => {
    const s = scriptsRef.current[name];
    if (s?.record) patch(name, { buffer: s.record.source ?? '' });
  };

  const format = async () => {
    const ok = (await editorRef.current?.format()) ?? false;
    if (ok) toasts.success('The code has been formatted successfully!');
    else toasts.error('The formatter is not available until the editor has loaded.');
  };

  const versionOf = (name: string): string | number | undefined => {
    const rec = scriptsRef.current[name]?.record;
    const summary = items.find((i) => i.name === name);
    if (rec && summary) return Number(rec.version) >= Number(summary.version) ? rec.version : summary.version;
    return rec?.version ?? summary?.version;
  };

  const toggle = async (name: string, enabled: boolean) => {
    if (!canWrite) return;
    setToggling((t) => new Set(t).add(name));
    try {
      const record = await client.data(ops.updateExtension, { path: { ...path, name }, body: { group: null, description: null, enabled, flags: null, message: null }, ifMatch: etagOfVersion(versionOf(name)) });
      setScripts((all) => {
        const cur = all[name];
        if (!cur?.record) return all;
        return { ...all, [name]: { ...cur, record: { ...cur.record, ...record, source: cur.record.source } } };
      });
      toasts.success(`${name} ${enabled ? 'enabled' : 'disabled'} — extensions reloaded`);
      refreshList();
      void reloadHost();
    } catch (e) {
      toasts.error(e instanceof ApiError && e.isVersionConflict ? `${name} was saved elsewhere since you opened it; the list is reloaded, try again.` : message(e));
      refreshList();
    } finally {
      setToggling((t) => {
        const next = new Set(t);
        next.delete(name);
        return next;
      });
    }
  };

  const remove = async (name: string) => {
    if (!canWrite) return;
    const ok = await confirm({ title: `Delete ${name}?`, body: `The script ${name} and its record are removed from the branch ${branch} (one commit; History and revert keep it).`, confirmLabel: 'Delete', tone: 'danger', typeToConfirm: name });
    if (!ok) return;
    setDeleting(true);
    try {
      await client.data(ops.deleteExtension, { path: { ...path, name }, ifMatch: etagOfVersion(versionOf(name)) });
      toasts.success(`Deleted ${name} — extensions reloaded`);
      await closeTab(name, true);
      refreshList();
      void reloadHost();
    } catch (e) {
      toasts.error(e instanceof ApiError && e.isVersionConflict ? `${name} was saved elsewhere since you opened it; reload it and try again.` : message(e));
    } finally {
      setDeleting(false);
    }
  };

  const created = (dto: ExtensionDto) => {
    setCreating(false);
    setScripts((s) => ({ ...s, [dto.name]: { record: dto, buffer: dto.source ?? '', loading: false, error: null, saving: false } }));
    setOpen((o) => (o.includes(dto.name) ? o : [...o, dto.name]));
    toasts.success(`Created ${dto.name} — extensions reloaded`);
    refreshList();
    navigate(at(dto.name));
    void reloadHost();
  };

  const reloadExtensions = async () => {
    setReloading(true);
    try {
      const s = await reloadHost();
      if (s?.kind === 'ready') toasts.success(`${s.extensions.length} extension${s.extensions.length === 1 ? '' : 's'} loaded${s.errors.length ? `; ${s.errors.length} failed — see the log` : ''}.`);
      else if (s?.kind === 'failed') toasts.error(`Extensions failed to load: ${s.message}`);
    } finally {
      setReloading(false);
    }
  };

  // ---- Run preSave on… -------------------------------------------------------------------------------------------------

  const runPreSave = async (subject: string, entities: ChangedEntity[]) => {
    if (!host) {
      toasts.error('The extension host is not running on this branch.');
      return;
    }
    setRunning(true);
    try {
      const outcomes = await host.preSave({ changedEntities: entities });
      const labels: Record<string, string> = {};
      for (const e of entities) {
        const doc = catalog.resolve(e.id) ?? catalog.resolver.get(e.id);
        if (doc) labels[e.id] = catalog.label(doc);
      }
      setReport({ subject, entities, outcomes, labels });
    } catch (e) {
      toasts.error(message(e));
    } finally {
      setRunning(false);
    }
  };

  const runOnDrafts = () => void runPreSave('the current drafts', changedEntitiesOf(workspace).changedEntities);

  // the user's document drafts as one `documents:batch` dry run (atomic: false, the operations Save All would send): the
  // server runs the schema and its validators and answers per operation; nothing is written
  const runOnServer = async () => {
    const drafts = workspace.all.filter((d) => d.kind === 'document');
    const docs = drafts.filter((d) => d.actor === 'user');
    const tempIds = new Set(drafts.filter((d) => isTempId(d.id)).map((d) => d.id));
    setRunning(true);
    try {
      let verdicts: ServerPreSaveRun['verdicts'] = [];
      if (docs.length > 0) {
        const result = await client.call(ops.batchDocuments, {
          path,
          body: { atomic: false, dryRun: true, message: `preSave on the server: ${docs.length} document${docs.length === 1 ? '' : 's'} (dry run)`, operations: docs.map((d) => operationOf(d, tempIds)) },
        });
        verdicts = result.data.results.flatMap((r) => {
          const d = docs[Number(r.index)];
          return d ? [verdictOf(d.id, d.label || d.id, r)] : [];
        });
      }
      setServerReport({ subject: 'the current drafts', verdicts });
    } catch (e) {
      toasts.error(message(e), { requestId: e instanceof ApiError ? e.requestId : undefined });
    } finally {
      setRunning(false);
    }
  };

  const runOnDocument = async (id: string | undefined) => {
    setPicking(false);
    if (!id) return;
    let doc = catalog.resolve(id) ?? catalog.resolver.get(id);
    if (!doc) {
      await catalog.resolver.load([id]);
      doc = catalog.resolver.get(id);
    }
    if (!doc) {
      toasts.error(`The document ${id} could not be read.`);
      return;
    }
    const entity: ChangedEntity = { unnyId: unnyIdOf(doc), id: doc.id, templateName: doc.template, changedParams: [], changes: [], isNew: false, isDeleted: false };
    await runPreSave(catalog.label(doc), [entity]);
  };

  // ---- render ------------------------------------------------------------------------------------------------------------

  const state = host?.state;
  const errors = state?.kind === 'ready' ? state.errors : [];
  const logs = host?.logs;
  const logCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const l of logs ?? []) m.set(l.source, (m.get(l.source) ?? 0) + 1);
    return m;
    // the host's log array is replaced on every entry; the version of useExtensionHost re-renders
  }, [logs, logs?.length]);
  const groups = useMemo(() => Array.from(new Set(items.map((i) => i.group).filter((g): g is string => !!g))).sort(), [items]);
  const modelTypes = useMemo(() => (catalog.loading ? undefined : modelTypings(catalog)), [catalog]);

  const current = active ? scripts[active] : undefined;
  const currentSummary = active ? items.find((i) => i.name === active) : undefined;
  const empty = list.data !== undefined && items.length === 0 && open.length === 0;

  const hostFailed = state?.kind === 'failed';

  return (
    <section className="scripts-page">
      <div className="page-head">
        <h1>Scripts</h1>
        <span className={`page-meta scripts-host-state ${hostFailed ? 'failed' : ''}`} data-testid="host-state" role="status">
          {hostFailed ? <AlertCircle className="icon-s" aria-hidden="true" /> : <span className={`status-dot ${state?.kind === 'ready' ? 'status-live' : 'status-off'}`} aria-hidden="true" />}
          {hostStateText(state)}
        </span>
        <span className="spacer" />
        <Button variant="ghost" busy={reloading} onClick={() => void reloadExtensions()} disabled={!host} icon={<RefreshCw aria-hidden="true" />}>
          Reload Extensions
        </Button>
        <Button variant="primary" aria-label="New script" onClick={() => setCreating(true)} missingPermission={missing} disabled={workspace.isProtected} title={protectedTitle} icon={<Plus aria-hidden="true" />}>
          Create
        </Button>
      </div>
      <div className="scripts-ide" data-testid="scripts-ide">
        <ScriptsTree
          items={items}
          active={active}
          logCounts={logCounts}
          errors={errors}
          dirty={dirty}
          canWrite={canWrite}
          toggling={toggling}
          onOpen={openScript}
          onToggle={(n, v) => void toggle(n, v)}
          header={
            <>
              {list.loading && list.data === undefined && (
                <div className="skeleton-rows" role="status" aria-label="Loading">
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="skeleton-row">
                      <span className="skeleton" />
                    </div>
                  ))}
                </div>
              )}
              {list.error !== undefined && (
                <div className="scripts-alert" role="alert">
                  <AlertCircle className="scripts-alert-icon" aria-hidden="true" />
                  <span>{message(list.error)}</span>
                </div>
              )}
            </>
          }
        />

        <div className="pane scripts-main">
          <div className="scripts-tabs" role="tablist" aria-label="Open scripts" data-testid="scripts-tabs">
            {open.map((n) => (
              <div key={n} role="tab" aria-selected={n === active} className={`scripts-tab${n === active ? ' active' : ''}`} data-testid={`script-tab-${n}`}>
                <button type="button" className="scripts-tab-name" onClick={() => openScript(n)}>
                  <FileCode2 className="icon-s" aria-hidden="true" />
                  {n}
                  {dirty.has(n) && <span className="scripts-dirty" title="Unsaved changes" aria-label="unsaved" role="img" />}
                </button>
                <IconButton label={`Close ${n}`} className="scripts-tab-close" onClick={() => void closeTab(n)}>
                  <X aria-hidden="true" />
                </IconButton>
              </div>
            ))}
            {open.length === 0 && <span className="scripts-tabs-empty muted small">No open scripts</span>}
          </div>

          {empty ? (
            <div className="scripts-empty">
              <EmptyState
                icon={<Puzzle />}
                title="No extensions on this branch yet."
                body="A script hooks into the editor: preSave validators, buttons and helpers."
                action={
                  <Button size="s" onClick={() => setCreating(true)} missingPermission={missing} disabled={workspace.isProtected} title={protectedTitle} icon={<Plus aria-hidden="true" />}>
                    Create one…
                  </Button>
                }
              />
            </div>
          ) : !active ? (
            <div className="scripts-empty">
              <EmptyState icon={<FileCode2 />} title="Pick a script in the tree." body="Each script opens in its own tab." />
            </div>
          ) : (
            <>
              <div className="scripts-actions" role="toolbar" aria-label="Script actions">
                <Button size="s" variant="primary" icon={<Save aria-hidden="true" />} busy={current?.saving} onClick={() => void save(active)} missingPermission={missing} disabled={workspace.isProtected || !current?.record || !dirty.has(active)} title={protectedTitle ?? 'Save (Ctrl+S): its own commit; the extensions reload'}>
                  Save
                </Button>
                <Button size="s" variant="ghost" icon={<RotateCcw aria-hidden="true" />} onClick={() => reset(active)} disabled={!dirty.has(active)} title="Back to the saved source">
                  Reset
                </Button>
                <Button size="s" variant="ghost" icon={<Braces aria-hidden="true" />} onClick={() => void format()} disabled={!current?.record || !canWrite}>
                  Format
                </Button>
                <Button size="s" variant="ghost" icon={<History aria-hidden="true" />} onClick={() => history.open('1')} disabled={!current?.record}>
                  History
                </Button>
                <Button size="s" variant="ghost" icon={<Play aria-hidden="true" />} busy={running} aria-haspopup="menu" onClick={(e) => setRunAnchor(e.currentTarget)}>
                  Run preSave on…
                </Button>
                <span className="scripts-actions-spacer" />
                {currentSummary && (
                  <span className="scripts-meta">
                    <span className={`status-dot ${currentSummary.enabled ? 'status-live' : 'status-off'}`}>{currentSummary.enabled ? 'enabled' : 'disabled'}</span>
                    <span className="mono">v{String(current?.record?.version ?? currentSummary.version)}</span>
                  </span>
                )}
                <Button size="s" variant="danger" icon={<Trash2 aria-hidden="true" />} busy={deleting} onClick={() => void remove(active)} missingPermission={missing} disabled={workspace.isProtected || !current?.record} title={protectedTitle}>
                  Delete
                </Button>
              </div>
              <Menu
                anchor={runAnchor}
                open={runAnchor !== null}
                onClose={() => setRunAnchor(null)}
                label="Run preSave on"
                items={[
                  { key: 'drafts', label: 'the current drafts', onSelect: runOnDrafts },
                  { key: 'document', label: 'a document…', onSelect: () => setPicking(true) },
                  { key: 'server', label: 'on the server', onSelect: () => void runOnServer() },
                ]}
              />

              {conflict === active && (
                <div className="banner banner-warning scripts-conflict" role="alert">
                  <AlertTriangle className="banner-icon" aria-hidden="true" />
                  <span>{active} was saved elsewhere since you opened it.</span>
                  <Button size="s" onClick={() => void reloadFromServer(active)}>
                    Reload
                  </Button>
                  <Button size="s" variant="danger" onClick={() => void save(active, true)} disabled={!canWrite}>
                    Overwrite
                  </Button>
                </div>
              )}
              {current?.record?.description && <p className="muted small scripts-description">{current.record.description}</p>}

              <div className="scripts-editor" data-testid="script-editor">
                {current?.error ? (
                  <div className="scripts-editor-error">
                    <EmptyState icon={<AlertCircle />} title="The script could not be opened" body={<span role="alert">{current.error}</span>} />
                  </div>
                ) : !current?.record ? (
                  <div className="code-editor-loading loading-line" role="status">
                    <span className="spinner" aria-hidden="true" />
                    Loading {active}…
                  </div>
                ) : (
                  <CodeEditor
                    ref={editorRef}
                    path={`file:///extensions/${active}.js`}
                    value={current.buffer}
                    onChange={(v) => patch(active, { buffer: v })}
                    readOnly={!canWrite}
                    onSave={() => void save(active)}
                    modelTypes={modelTypes}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {creating && <CreateScriptDialog branch={branch} groups={groups} existing={items.map((i) => i.name)} onClose={() => setCreating(false)} onCreated={created} />}
      {history.value === '1' && active && <HistoryDrawer branch={branch} name={active} onClose={history.close} />}
      {picking && <ReferencePicker param={{ template: null, name: 'document', displayName: 'Document', type: 'document' }} selected={[]} onClose={() => setPicking(false)} onPick={(ids) => void runOnDocument(ids[0])} />}
      {report && <PreSaveReport run={report} onClose={() => setReport(null)} />}
      {serverReport && <ServerPreSaveReport run={serverReport} onClose={() => setServerReport(null)} />}
    </section>
  );
}
